import { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useResizeDetector } from "react-resize-detector";
import {
	XYPlot,
	LineSeries,
	HorizontalGridLines,
	VerticalGridLines,
	XAxis,
	YAxis,
	DiscreteColorLegend,
	Borders,
	VerticalBarSeries,
	Highlight,
	MarkSeries,
} from "react-vis";

import { getByDotNotation } from "./utils.js";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		height: "100%",
		background: "white",
		color: theme.palette.primary.main,
		padding: "1%",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
	},
}));

const calculateLegendPosition = (legendPosition) => ({
	top: legendPosition.includes("Top") ? "0px" : "",
	bottom: legendPosition.includes("Bottom") ? "0px" : "",
	left: legendPosition.includes("Left") ? "0px" : "",
	right: legendPosition.includes("Right") ? "0px" : "",
});

const generateValues = () => {
	const data = [];
	for (let i = 0; i < Math.floor(Math.random() * (10 - 5) + 5); i += 1) {
		data.push({
			x: i,
			y: Math.random(),
		});
	}

	return data.sort((a, b) => a.x - b.x);
};

const Plot = ({
	itemWidth,
	itemHeight,
	variables,
	// calculateFontSize,
	mode,
	liveVariables = {},
}) => {
	const classes = useStyles();
	const [width, setWidth] = useState(itemWidth || 500);
	const [height, setHeight] = useState(itemHeight || 500);
	const [data, setData] = useState([]);
	const [slicedData, setSlicedData] = useState([]);
	const [lastDrawLocation, setLastDrawLocation] = useState(null);

	const onResize = ({ width: elementWidth, height: elementHeight }) => {
		setWidth(elementWidth);
		setHeight(elementHeight);
	};

	const { ref: resizeRef } = useResizeDetector({
		refreshMode: "debounce",
		refreshRate: 50,
		onResize,
	});

	useEffect(() => {
		if (mode === "edit") {
			setData(generateValues());
		}
	}, [mode]);

	useEffect(() => {
		if (mode === "view") {
			let newLiveData = getByDotNotation(liveVariables, variables?.variable?.value);
			if (!newLiveData) {
				newLiveData = liveVariables[variables?.variable?.value];
			}

			if (Array.isArray(newLiveData)) {
				setData((prevData) => [...prevData, ...(newLiveData.map((d) => ({
					x: Date.now(),
					y: d,
				})))]);
			} else if (newLiveData) {
				setData((prevData) => [...prevData, {
					x: Date.now(),
					y: newLiveData,
				}]);
			}
		}
	}, [mode, liveVariables, variables?.variable?.value]);

	useEffect(() => {
		if (data.length > 0) {
			const maxValues = variables?.maxValues?.value || 5;
			setSlicedData(data.slice(-maxValues));
		}
	}, [data, variables?.maxValues?.value]);

	return (
		<Grid
			ref={resizeRef}
			item
			className={classes.root}
			style={{
				fontSize: "14px",
			}}
		>
			<XYPlot
				height={height - 60}
				width={width - 50}
				margin={{ bottom: 20, left: 40, right: 0, top: 0, position: "relative" }}
				xDomain={lastDrawLocation ? [lastDrawLocation.left, lastDrawLocation.right] : null}
				yDomain={lastDrawLocation ? [lastDrawLocation.bottom, lastDrawLocation.top] : null}
			>
				<VerticalGridLines />
				<HorizontalGridLines />
				{variables?.chartType?.value === "Line" && (
					<LineSeries
						data={slicedData}
						color={variables?.color?.value || "#0000FF"}
						animation="gentle"
					/>
				)}
				{variables?.chartType?.value === "Bar" && (
					<VerticalBarSeries
						data={slicedData}
						color={variables?.color?.value || "#0000FF"}
						animation="gentle"
						barWidth={0.2}
					/>
				)}
				{variables?.chartType?.value === "Scatter" && (
					<MarkSeries
						data={slicedData}
						color={variables?.color?.value || "#0000FF"}
						animation="gentle"
						barWidth={0.2}
					/>
				)}
				<Borders style={{
					bottom: { fill: "#fff" },
					left: { fill: "#fff" },
					right: { fill: "#fff" },
					top: { fill: "#fff" },
				}}
				/>
				<XAxis tickFormat={(v) => v.toFixed(0)} tickLabelAngle={-45} tickValues={data.map((d) => d.x)} title={variables?.xAxisLabel?.value || ""} />
				<YAxis tickFormat={(v) => v.toFixed(2)} title={variables?.yAxisLabel?.value || ""} />
				{variables?.showLegend?.value
					&& (
						<DiscreteColorLegend
							items={[{ title: variables?.plotName?.value || "Plot", color: variables?.color?.value || "#0000FF" }]}
							style={{
								position: "absolute",
								...calculateLegendPosition(variables?.legendPosition?.value || "top_right"),
							}}
						/>
					)}
				<Highlight
					onBrushEnd={(area) => setLastDrawLocation(area)}
					onDrag={(area) => {
						setLastDrawLocation({
							bottom: lastDrawLocation?.bottom || 0 + (area.top - area.bottom),
							top: lastDrawLocation?.top || 0 + (area.top - area.bottom),
							left: lastDrawLocation?.left || 0 - (area.right - area.left),
							right: lastDrawLocation?.right || 0 - (area.right - area.left),
						});
					}}
				/>
			</XYPlot>
		</Grid>
	);
};

export default Plot;
